import React, { useCallback } from "react";
import { Text, View, Link } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import { handleClick } from "lib/utils/navigator";

export const TemplateTwoPDFSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) =>{
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  return (
  <div
  onClick={handleClickCallback}
    style={{
      ...styles.flexCol,
      gap: spacing["2"],
      marginTop: spacing["5"],
      cursor:"pointer"
      // ...style,
    }}
  >
    {heading && (
      <View style={{ ...styles.flexCol, gap: spacing["1"] }}>
        <Text
          style={{
            fontWeight: "bold",
            letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
            color: themeColor || DEFAULT_FONT_COLOR,
          }}
          debug={DEBUG_RESUME_PDF_FLAG}
        >
          {heading}
        </Text>
      </View>
    )}
    {children}
  </div>
)};

// Enhanced version with optimized spacing and orphan heading prevention
export const TemplateTwoPDFSectionWithSubsections = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(() => handleClick(heading), [heading]);
  
  // Group content to prevent orphan headings
  const groupedChildren = React.Children.toArray(children);
  const firstChild = groupedChildren[0];
  const restChildren = groupedChildren.slice(1);
  
  return (
    <div
      onClick={handleClickCallback}
      style={{
        ...styles.flexCol,
        gap: "3pt", // Reduced from spacing["2"] (24pt) to 3pt
        marginTop: "15pt", // Reduced from spacing["5"] (60pt) to 15pt
        cursor: "pointer"
      }}
    >
      {/* Wrap heading with first child only to prevent orphan headings */}
      {heading && firstChild && (
        <View
          style={{
            ...styles.flexCol,
            gap: "3pt",
          }}
          wrap={false}
        >
          {/* Heading */}
          <View style={{ ...styles.flexCol, gap: "3pt" }}>
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: themeColor || DEFAULT_FONT_COLOR,
                lineHeight: "1.2",
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
          </View>
          {/* First child inside the same container */}
          {firstChild}
        </View>
      )}
      
      {/* Heading only (when no children) */}
      {heading && !firstChild && (
        <View style={{ ...styles.flexCol, gap: "3pt" }}>
          <Text
            style={{
              fontWeight: "bold",
              letterSpacing: "0.3pt",
              color: themeColor || DEFAULT_FONT_COLOR,
              lineHeight: "1.2",
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {heading}
          </Text>
        </View>
      )}
      
      {/* Rest of children - can break across pages */}
      {restChildren.length > 0 && (
        <View style={{ ...styles.flexCol, gap: "3pt" }}>
          {restChildren}
        </View>
      )}
    </div>
  );
};
